import { ACCOUNT_URL } from "@elyxeer/ui";
import AlarmOutlinedIcon from "@mui/icons-material/AlarmOutlined";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ContrastOutlinedIcon from "@mui/icons-material/ContrastOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
// English is held back until release — restore with the "language" row below.
// import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import type { ComponentType } from "react";

import type { LegalSlug } from "~/content/legal";
import { LEGAL_LABEL_KEYS, legalPath } from "~/lib/legal";

/**
 * The settings table.
 *
 * Pure data, like `shell/navigation.ts`: the profile menu renders from it and each entry
 * names either an in-app detail screen or an external account action. Keeping both
 * shapes here prevents the account menu and its destinations from drifting apart.
 */
interface SettingsItemBase {
  key:
    | "membership"
    | "email"
    | "username"
    | "notifications"
    | "reminders"
    | "language"
    | "appearance"
    | "legal"
    | "deleteAccount"
    | LegalSlug;
  /** i18n key for the row label. Resolve with `t()` at the call site. */
  labelKey: string;
  icon: ComponentType<SvgIconProps>;
}

interface RouteSettingsItem extends SettingsItemBase {
  kind: "route";
  path: string;
}

interface ExternalSettingsItem extends SettingsItemBase {
  kind: "external";
  url: string;
}

export type SettingsAction = "delete-account";

interface ActionSettingsItem extends SettingsItemBase {
  kind: "action";
  action: SettingsAction;
  tone: "danger";
}

export type SettingsItem =
  RouteSettingsItem | ExternalSettingsItem | ActionSettingsItem;

export const SETTINGS_ITEMS: SettingsItem[] = [
  {
    key: "email",
    kind: "route",
    path: "/profile/email",
    labelKey: "profile.menu.email",
    icon: AlternateEmailOutlinedIcon,
  },
  {
    key: "username",
    kind: "route",
    path: "/profile/username",
    labelKey: "profile.menu.username",
    icon: BadgeOutlinedIcon,
  },
  {
    key: "notifications",
    kind: "route",
    path: "/profile/notifications",
    labelKey: "profile.menu.notifications",
    icon: NotificationsNoneOutlinedIcon,
  },
  {
    key: "reminders",
    kind: "route",
    path: "/profile/reminders",
    labelKey: "profile.menu.reminders",
    icon: AlarmOutlinedIcon,
  },
  // Hidden while the member app ships Czech only; English is released later.
  // {
  //   key: "language",
  //   kind: "route",
  //   path: "/profile/language",
  //   labelKey: "profile.menu.language",
  //   icon: TranslateOutlinedIcon,
  // },
  {
    key: "appearance",
    kind: "route",
    path: "/profile/appearance",
    labelKey: "profile.menu.appearance",
    icon: ContrastOutlinedIcon,
  },
  {
    key: "membership",
    kind: "external",
    url: ACCOUNT_URL,
    labelKey: "profile.menu.membership",
    icon: WorkspacePremiumOutlinedIcon,
  },
  {
    key: "deleteAccount",
    kind: "action",
    action: "delete-account",
    tone: "danger",
    labelKey: "profile.menu.deleteAccount",
    icon: DeleteForeverOutlinedIcon,
  },
];

export const LEGAL_SECTION_ITEM: RouteSettingsItem = {
  key: "legal",
  kind: "route",
  path: "/profile/legal",
  labelKey: "legal.title",
  icon: DescriptionOutlinedIcon,
};

/** Documents live on a dedicated page to distinguish them from account actions. */
export const LEGAL_ITEMS: SettingsItem[] = [
  {
    key: "terms",
    kind: "route",
    path: legalPath("terms"),
    labelKey: LEGAL_LABEL_KEYS.terms,
    icon: DescriptionOutlinedIcon,
  },
  {
    key: "privacy",
    kind: "route",
    path: legalPath("privacy"),
    labelKey: LEGAL_LABEL_KEYS.privacy,
    icon: PrivacyTipOutlinedIcon,
  },
  {
    key: "account-deletion",
    kind: "route",
    path: legalPath("account-deletion"),
    labelKey: LEGAL_LABEL_KEYS["account-deletion"],
    icon: DeleteSweepOutlinedIcon,
  },
];
